import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReceivedBseBidDto,
  useEvaluateBidsWithAiMutation,
  useEvaluateSingleBidWithAiMutation,
  useGenerateRequirementsWithAiMutation,
} from "../../../../../../autogen/bff-api";
import { getPreferredLanguage } from "../../../../../../common/local-storage/language";
import {
  BidEvaluation,
  ComparisonResult,
  comparisonSchema,
  createSingleBidEvaluationSchema,
  ProductGroup,
  RequirementsResult,
  requirementsSchema,
  SingleEvaluationResult,
} from "../../../../../../common/types";
import { getTotalPrice } from "../BidEvaluationUtils";

export const getLanguageInstruction = (): string => {
  const currentLanguage = getPreferredLanguage();
  return currentLanguage === "No" ? "Respond in Norwegian." : "Respond in English.";
};

export const parseRequirementsResult = (content: string): RequirementsResult | null =>
  JSON.parse(content) as RequirementsResult;

export const parseSingleEvaluation = ({ bidId, content }: { bidId: string; content: string }): BidEvaluation => {
  const parsedResult = JSON.parse(content) as SingleEvaluationResult;
  return {
    ...parsedResult.evaluation,
    bidId,
  };
};

export const parseComparisonResult = (content: string): ComparisonResult | null =>
  JSON.parse(content) as ComparisonResult;

export const useAIEvaluation = ({
  sourcingEventId,
  negotiationRoundId,
  bids,
  productGroups,
}: {
  sourcingEventId?: string;
  negotiationRoundId?: string;
  bids: ReceivedBseBidDto[];
  productGroups?: ProductGroup[];
}) => {
  const [
    generateRequirementsWithAi,
    { data: requirementsData, isLoading: isGeneratingRequirements, error: requirementsError },
  ] = useGenerateRequirementsWithAiMutation();

  const [evaluateSingleBidWithAi, { isLoading: isEvaluatingSingleBid, error: singleBidEvaluationError }] =
    useEvaluateSingleBidWithAiMutation();

  const [evaluateBidsWithAi, { isLoading: isComparingBids, error: evaluationError }] = useEvaluateBidsWithAiMutation();

  const [individualEvaluations, setIndividualEvaluations] = useState<BidEvaluation[]>([]);
  const [comparison, setComparison] = useState<ComparisonResult>();

  const evaluateAndParseSingleBid = useCallback(
    async ({ bid, requirements }: { bid: ReceivedBseBidDto; requirements: string[] }): Promise<BidEvaluation> => {
      const evaluationRes = await evaluateSingleBidWithAi({
        evaluateSingleBidWithAiRequest: {
          bidId: bid.id,
          prompt: `Evaluate this bid strictly against the sourcing event requirements using this EXACT list: ${JSON.stringify(
            requirements
          )}. Include ALL requirements above in the 'requirements' array (same wording); set 'fulfilled' to "yes", "no", or "partly" based on how well 
                  the requirement is met. Do NOT introduce other requirements. If a requirement is not applicable to the bid, set 'fulfilled' to "no".
        
                Also evaluate the total price of this bid. 
                The sum of the product prices in the bid is ${productGroups ? getTotalPrice({ bid, groups: productGroups }) : "not provided"}.
                If you find another price in the bid that seems to include more than the product price sum, use that one instead.
        
                Provide strengths, weaknesses, requirement fulfillment, price analysis, and an overall recommendation. Score MUST be an integer 0-100. ${getLanguageInstruction()}`,
          jsonSchema: createSingleBidEvaluationSchema(),
        },
      }).unwrap();
      const lastMessage = evaluationRes.messages[evaluationRes.messages.length - 1];
      return parseSingleEvaluation({ bidId: bid.id, content: lastMessage.content });
    },
    [evaluateSingleBidWithAi, productGroups]
  );

  const runEvaluation = useCallback(async (): Promise<void> => {
    setIndividualEvaluations([]);
    setComparison(undefined);

    if (!sourcingEventId) return;

    const languageInstruction = getLanguageInstruction();
    const requirementsRes = await generateRequirementsWithAi({
      generateRequirementsWithAiRequest: {
        sourcingEventId,
        prompt: `From the sourcing event, produce a
            list of at most 10 high-level, GENERIC evaluation requirements to be used for ALL bids. 
            The requirements must be broadly applicable and phrased so suppliers are likely to provide 
            information for them. Avoid bid-specific or overly narrow details. Merge overlapping 
            requirements and prioritize the most important criteria. Return only the list in the schema. 
            ${languageInstruction}`,
        jsonSchema: requirementsSchema,
      },
    }).unwrap();

    const parsedRequirements = requirementsRes?.messages?.length
      ? parseRequirementsResult(requirementsRes.messages[requirementsRes.messages.length - 1].content)
      : undefined;
    if (!parsedRequirements) throw new Error("Failed to generate evaluation requirements");

    const bidEvaluations = await Promise.all(
      bids.map((bid) => evaluateAndParseSingleBid({ bid, requirements: parsedRequirements.requirements }))
    );
    setIndividualEvaluations(bidEvaluations);

    const evaluationRes = await evaluateBidsWithAi({
      evaluateBidsWithAiRequest: {
        sourcingEventId: sourcingEventId,
        negotiationRoundId,
        prompt: `Compare the submitted bids using this list of evaluation requirements:
          ${JSON.stringify(parsedRequirements)}. Apply the same requirements to every bid. 
          Summarize key insights and provide an overall recommendation based on the requirements set.
          If at least one bid is delivered, set the best bid to the bid with the highest score.
    
          Do not refer directly to the stand-alone, individual bid evaluations, but make sure that the comparison is consistent 
          with them.
    
          Do not mention ids, for instance bid ids, in sentences - the response should be human readable.
          It is very important that the bidId properties are the actual bid id UUID.
    
          ${languageInstruction}`,
        jsonSchema: comparisonSchema,
      },
    }).unwrap();
    if (!evaluationRes.messages?.length) throw new Error("Empty response from evaluateBidsWithAi");
    const lastMessage = evaluationRes.messages[evaluationRes.messages.length - 1];
    const parsedComparison = parseComparisonResult(lastMessage.content);
    if (parsedComparison?.summary) setComparison(parsedComparison);
  }, [
    bids,
    evaluateAndParseSingleBid,
    evaluateBidsWithAi,
    generateRequirementsWithAi,
    negotiationRoundId,
    sourcingEventId,
  ]);

  useEffect(() => {
    if (bids.length === 0) return;
    if (bids.some((bid) => (bid.forNegotiationRound ?? undefined) !== negotiationRoundId)) return;

    runEvaluation();
  }, [sourcingEventId, bids, runEvaluation, negotiationRoundId]);

  const requirements = useMemo(() => {
    const messages = requirementsData?.messages;
    if (messages?.length) {
      const lastMessage = messages[messages.length - 1];
      const parsedRequirements = parseRequirementsResult(lastMessage.content);
      return parsedRequirements?.requirements || [];
    }
  }, [requirementsData?.messages]);

  return {
    requirements,
    individualEvaluations,
    comparison,
    isLoading: isEvaluatingSingleBid || isGeneratingRequirements || isComparingBids,
    isEvaluatingSingleBid,
    isGeneratingRequirements,
    isComparingBids,
    error: requirementsError || evaluationError || singleBidEvaluationError,
  };
};
